// @ts-ignore
import ELK from 'elkjs/lib/elk.bundled.js';
import { Edge, Node, Position } from 'reactflow';

const elk = new ELK();

const NODE_W = 192;
const NODE_H = 123;

/** Horizontal gap between sequential layers inside one flow. */
const LAYER_GAP = 140;
/** Vertical gap between sibling / branch nodes in the same layer. */
const SIBLING_GAP = 88;
/** Vertical gap between separate flows stacked on the canvas. */
const FLOW_GAP = 120;
/** Canvas padding around the whole layout. */
const CANVAS_PAD = 64;
/** Gap between an AI Agent and its tool column. */
const TOOL_GAP_X = 96;
/** Vertical gap between stacked AI tools. */
const TOOL_GAP_Y = 48;
/** Minimum vertical separation between parallel branch targets. */
const BRANCH_MIN_GAP = 96;

const TRIGGER_ACTIONS = new Set([
   'start', 'webhook',
   'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
   'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
   'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger', 'teamsTrigger', 'sharepointTrigger', 
   'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger', 'gmailTrigger',
   'oneDriveTrigger',
]);

const NOTE_ACTIONS = new Set(['note', 'comment']);

export type LayoutDirection = 'LR' | 'TB';

type ComponentInfo = {
   id: string;
   nodeIds: string[];
   score: number;
   avgY: number;
   label: string;
};

function nodeSize(n: Node): { w: number; h: number } {
   return {
      w: Math.max(120, n.width ?? NODE_W),
      h: Math.max(80, n.height ?? NODE_H),
   };
}

function isToolEdge(e: Edge): boolean {
   const handle = String(e.sourceHandle || '');
   return handle === 'Tools' || handle.toLowerCase() === 'tools';
}

function isNoteNode(n: Node): boolean {
   return NOTE_ACTIONS.has(String(n.data?.action || ''));
}

function handleRank(handle?: string | null): number {
   const h = String(handle || '').toLowerCase();
   if (!h) return 50;
   if (h.includes('true') || h === 'output-true' || h.includes('yes')) return 0;
   if (h.includes('false') || h === 'output-false' || h.includes('no')) return 100;
   if (h === 'tools') return 80;
   if (h.includes('output')) return 40;
   if (h.includes('input')) return 20;
   return 50;
}

/**
 * Collect descendants of `rootId` that are NOT also reachable from `blockedRoots`
 * (so shared merge nodes / other branches don't get yanked).
 */
function exclusiveDescendants(
   rootId: string,
   blockedRoots: Set<string>,
   outgoing: Map<string, string[]>
): string[] {
   const blocked = new Set<string>();
   const visitBlock = (id: string) => {
      if (blocked.has(id)) return;
      blocked.add(id);
      for (const next of outgoing.get(id) || []) visitBlock(next);
   };
   for (const b of blockedRoots) {
      if (b !== rootId) visitBlock(b);
   }

   const result: string[] = [];
   const stack = [rootId];
   const seen = new Set<string>([rootId]);
   while (stack.length) {
      const cur = stack.pop()!;
      for (const next of outgoing.get(cur) || []) {
         if (seen.has(next) || blocked.has(next)) continue;
         seen.add(next);
         result.push(next);
         stack.push(next);
      }
   }
   return result;
}

/**
 * When one node fans out to 2+ different targets (routing True/False, parallel paths),
 * stack those targets vertically instead of leaving them on one flat line.
 * Moves each branch's exclusive downstream with it.
 */
function separateBranchTargets(
   positions: Map<string, { x: number; y: number }>,
   componentNodes: Node[],
   flowEdges: Edge[]
) {
   const nodeById = new Map(componentNodes.map((n) => [n.id, n]));
   const outgoingEdges = new Map<string, Edge[]>();
   const outgoingIds = new Map<string, string[]>();
   for (const e of flowEdges) {
      const list = outgoingEdges.get(e.source) || [];
      list.push(e);
      outgoingEdges.set(e.source, list);
      const ids = outgoingIds.get(e.source) || [];
      if (!ids.includes(e.target)) ids.push(e.target);
      outgoingIds.set(e.source, ids);
   }

   for (const [sourceId, edgesFrom] of outgoingEdges) {
      const sourcePos = positions.get(sourceId);
      if (!sourcePos) continue;

      const byTarget = new Map<string, Edge[]>();
      for (const e of edgesFrom) {
         const list = byTarget.get(e.target) || [];
         list.push(e);
         byTarget.set(e.target, list);
      }
      if (byTarget.size < 2) continue;

      const targets = [...byTarget.entries()]
         .map(([targetId, eds]) => {
            const best = eds
               .slice()
               .sort((a, b) => handleRank(a.sourceHandle) - handleRank(b.sourceHandle))[0];
            return {
               targetId,
               rank: handleRank(best?.sourceHandle),
               y: positions.get(targetId)?.y ?? 0,
            };
         })
         .sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            return a.y - b.y;
         });

      // Already vertically separated enough? leave alone.
      const ys = targets.map((t) => t.y);
      if (Math.max(...ys) - Math.min(...ys) >= BRANCH_MIN_GAP) continue;

      const sizes = targets.map((t) => nodeSize(nodeById.get(t.targetId)!));
      const totalH =
         sizes.reduce((s, sz) => s + sz.h, 0) + BRANCH_MIN_GAP * Math.max(0, targets.length - 1);

      const sourceH = nodeSize(nodeById.get(sourceId)!).h;
      let curY = sourcePos.y + sourceH / 2 - totalH / 2;
      const rootSet = new Set(targets.map((t) => t.targetId));

      for (let i = 0; i < targets.length; i++) {
         const id = targets[i].targetId;
         const prev = positions.get(id);
         if (!prev) continue;
         const h = sizes[i].h;
         const dy = curY - prev.y;
         positions.set(id, { x: prev.x, y: curY });

         if (dy !== 0) {
            for (const desc of exclusiveDescendants(id, rootSet, outgoingIds)) {
               const p = positions.get(desc);
               if (!p) continue;
               positions.set(desc, { x: p.x, y: p.y + dy });
            }
         }
         curY += h + BRANCH_MIN_GAP;
      }
   }
}

/**
 * When several different sources feed one node (merge), stack those sources vertically
 * so inbound edges don't sit on one crushed horizontal band.
 */
function separateMergeSources(
   positions: Map<string, { x: number; y: number }>,
   componentNodes: Node[],
   flowEdges: Edge[]
) {
   const nodeById = new Map(componentNodes.map((n) => [n.id, n]));
   const incoming = new Map<string, Edge[]>();
   for (const e of flowEdges) {
      const list = incoming.get(e.target) || [];
      list.push(e);
      incoming.set(e.target, list);
   }

   for (const [targetId, edgesIn] of incoming) {
      const targetPos = positions.get(targetId);
      if (!targetPos) continue;

      const bySource = new Map<string, Edge[]>();
      for (const e of edgesIn) {
         const list = bySource.get(e.source) || [];
         list.push(e);
         bySource.set(e.source, list);
      }
      if (bySource.size < 2) continue;

      const sources = [...bySource.keys()]
         .map((sourceId) => ({
            sourceId,
            y: positions.get(sourceId)?.y ?? 0,
            x: positions.get(sourceId)?.x ?? 0,
         }))
         .sort((a, b) => a.y - b.y || a.x - b.x);

      const ys = sources.map((s) => s.y);
      const spread = Math.max(...ys) - Math.min(...ys);
      if (spread >= BRANCH_MIN_GAP * 0.75) continue;

      const sizes = sources.map((s) => nodeSize(nodeById.get(s.sourceId)!));
      const totalH =
         sizes.reduce((s, sz) => s + sz.h, 0) + BRANCH_MIN_GAP * Math.max(0, sources.length - 1);
      const targetH = nodeSize(nodeById.get(targetId)!).h;
      let curY = targetPos.y + targetH / 2 - totalH / 2;

      for (let i = 0; i < sources.length; i++) {
         const id = sources[i].sourceId;
         const prev = positions.get(id)!;
         const h = sizes[i].h;
         positions.set(id, { x: prev.x, y: curY });
         curY += h + BRANCH_MIN_GAP;
      }
   }
}

/**
 * Undirected connected components. Tool edges still count so agent+tools stay together.
 */
function findConnectedComponents(nodes: Node[], edges: Edge[]): string[][] {
   const ids = new Set(nodes.map((n) => n.id));
   const adj = new Map<string, Set<string>>();
   for (const id of ids) adj.set(id, new Set());

   for (const e of edges) {
      if (!ids.has(e.source) || !ids.has(e.target)) continue;
      adj.get(e.source)!.add(e.target);
      adj.get(e.target)!.add(e.source);
   }

   const seen = new Set<string>();
   const components: string[][] = [];

   for (const id of ids) {
      if (seen.has(id)) continue;
      const stack = [id];
      const group: string[] = [];
      seen.add(id);
      while (stack.length) {
         const cur = stack.pop()!;
         group.push(cur);
         for (const next of adj.get(cur) || []) {
            if (seen.has(next)) continue;
            seen.add(next);
            stack.push(next);
         }
      }
      components.push(group);
   }

   return components;
}

/**
 * Rank flows so the canvas reads top→bottom in a sensible product order.
 * Lower score = higher on the canvas.
 */
function scoreComponent(nodes: Node[]): { score: number; label: string } {
   const actions = nodes.map((n) => String(n.data?.action || ''));
   const names = nodes.map((n) => `${n.data?.name || ''} ${n.data?.index || ''}`).join(' ').toLowerCase();

   if (actions.some((a) => a === 'whatsappListener' || a === 'whatsappCloudTrigger')) {
      return { score: 10, label: 'WhatsApp chat' };
   }
   if (actions.some((a) => a === 'googleFormTrigger' || a === 'typeformTrigger')) {
      return { score: 20, label: 'Form intake' };
   }
   if (actions.some((a) => TRIGGER_ACTIONS.has(a))) {
      return { score: 30, label: 'Trigger flow' };
   }
   if (/waiting|waitlist/.test(names) || /event|reservation|reminder|result/.test(names)) {
      return { score: 40, label: 'Scheduler / ops' };
   }
   if (/document|docs/.test(names)) {
      return { score: 50, label: 'Documents' };
   }
   if (nodes.every(isNoteNode)) {
      return { score: 900, label: 'Notes' };
   }
   if (nodes.length === 1) {
      return { score: 800, label: 'Orphan' };
   }
   return { score: 100, label: 'Flow' };
}

function orderComponents(components: string[][], nodeById: Map<string, Node>): ComponentInfo[] {
   return components
      .map((nodeIds, i) => {
         const nodes = nodeIds.map((id) => nodeById.get(id)!).filter(Boolean);
         const { score, label } = scoreComponent(nodes);
         const avgY =
            nodes.reduce((s, n) => s + (n.position?.y ?? 0), 0) / Math.max(1, nodes.length);
         return { id: `c${i}`, nodeIds, score, avgY, label };
      })
      .sort((a, b) => {
         if (a.score !== b.score) return a.score - b.score;
         return a.avgY - b.avgY;
      });
}

/**
 * Layout one connected flow left→right with ELK.
 * AI Agent tool edges are excluded from ELK and placed in a tidy column beside the agent.
 */
async function layoutComponent(
   componentNodes: Node[],
   allEdges: Edge[],
   direction: LayoutDirection
): Promise<Map<string, { x: number; y: number }>> {
   const ids = new Set(componentNodes.map((n) => n.id));
   const componentEdges = allEdges.filter(
      (e) => ids.has(e.source) && ids.has(e.target)
   );
   const toolEdges = componentEdges.filter(isToolEdge);
   const flowEdges = componentEdges.filter((e) => !isToolEdge(e));

   const toolTargetIds = new Set(toolEdges.map((e) => e.target));
   // Tools that are ONLY reached via Tools handle — place manually next to agent.
   const incomingFlow = new Map<string, number>();
   for (const e of flowEdges) {
      incomingFlow.set(e.target, (incomingFlow.get(e.target) || 0) + 1);
   }
   const pureToolIds = new Set(
      [...toolTargetIds].filter((id) => !(incomingFlow.get(id) || 0))
   );

   const elkNodes = componentNodes.filter((n) => !pureToolIds.has(n.id));
   const positions = new Map<string, { x: number; y: number }>();

   if (!elkNodes.length) {
      // Degenerate: only tools / notes
      let y = 0;
      for (const n of componentNodes) {
         positions.set(n.id, { x: 0, y });
         y += nodeSize(n).h + SIBLING_GAP;
      }
      return positions;
   }

   const isHorizontal = direction === 'LR';
   // Stable sibling order: keep previous top→bottom reading order inside layers.
   const elkNodesSorted = [...elkNodes].sort((a, b) => {
      const dy = (a.position?.y ?? 0) - (b.position?.y ?? 0);
      if (dy !== 0) return dy;
      return (a.position?.x ?? 0) - (b.position?.x ?? 0);
   });

   const elkGraph = {
      id: 'flow',
      layoutOptions: {
         'elk.algorithm': 'layered',
         'elk.direction': isHorizontal ? 'RIGHT' : 'DOWN',

         'elk.spacing.nodeNode': String(SIBLING_GAP),
         'elk.layered.spacing.nodeNodeBetweenLayers': String(LAYER_GAP),
         'elk.layered.spacing.edgeNodeBetweenLayers': '28',
         'elk.layered.spacing.edgeEdgeBetweenLayers': '18',

         'elk.edgeRouting': 'ORTHOGONAL',
         'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
         // BRANDES_KOEPF + BALANCED spreads branch siblings vertically instead of
         // collapsing True/False targets onto one flat line.
         'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
         'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
         'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
         'elk.layered.cycleBreaking.strategy': 'GREEDY',

         'elk.padding': '[top=16,left=16,bottom=16,right=16]',
         'elk.separateConnectedComponents': 'false',
      },
      children: elkNodesSorted.map((n) => {
         const { w, h } = nodeSize(n);
         return {
            id: n.id,
            width: w,
            height: h,
         };
      }),
      edges: flowEdges
         .filter((e) => !pureToolIds.has(e.source) && !pureToolIds.has(e.target))
         // Prefer True-before-False model order so ELK ranks upper branch first.
         .slice()
         .sort((a, b) => {
            if (a.source === b.source) {
               return handleRank(a.sourceHandle) - handleRank(b.sourceHandle);
            }
            return 0;
         })
         .map((e) => ({
            id: e.id,
            sources: [e.source],
            targets: [e.target],
         })),
   };

   try {
      const laid = await elk.layout(elkGraph);
      for (const c of laid.children ?? []) {
         positions.set(c.id, { x: c.x ?? 0, y: c.y ?? 0 });
      }
   } catch (err) {
      console.warn('[autoLayout] ELK component layout failed, using grid fallback:', err);
      let x = 0;
      for (const n of elkNodes) {
         positions.set(n.id, { x, y: 0 });
         x += nodeSize(n).w + LAYER_GAP;
      }
   }

   const layoutFlowEdges = flowEdges.filter(
      (e) => !pureToolIds.has(e.source) && !pureToolIds.has(e.target)
   );
   // Fan out parallel branches / merge inputs so they don't sit on one crushed line.
   separateBranchTargets(positions, componentNodes, layoutFlowEdges);
   separateMergeSources(positions, componentNodes, layoutFlowEdges);

   // Place AI tools in a vertical column to the right of their agent.
   const toolsByAgent = new Map<string, string[]>();
   for (const e of toolEdges) {
      if (!pureToolIds.has(e.target)) continue;
      const list = toolsByAgent.get(e.source) || [];
      list.push(e.target);
      toolsByAgent.set(e.source, list);
   }

   for (const [agentId, toolIds] of toolsByAgent) {
      const agentPos = positions.get(agentId);
      const agentNode = componentNodes.find((n) => n.id === agentId);
      if (!agentPos || !agentNode) continue;

      const agentH = nodeSize(agentNode).h;
      const agentW = nodeSize(agentNode).w;

      // Stable order: previous Y, then name
      const ordered = [...toolIds].sort((a, b) => {
         const na = componentNodes.find((n) => n.id === a);
         const nb = componentNodes.find((n) => n.id === b);
         const ya = na?.position?.y ?? 0;
         const yb = nb?.position?.y ?? 0;
         if (ya !== yb) return ya - yb;
         return String(na?.data?.name || a).localeCompare(String(nb?.data?.name || b));
      });

      const totalH =
         ordered.reduce((s, id) => {
            const n = componentNodes.find((x) => x.id === id)!;
            return s + nodeSize(n).h;
         }, 0) +
         TOOL_GAP_Y * Math.max(0, ordered.length - 1);

      let curY = agentPos.y + agentH / 2 - totalH / 2;
      const toolX = agentPos.x + agentW + TOOL_GAP_X;

      for (const toolId of ordered) {
         const toolNode = componentNodes.find((n) => n.id === toolId)!;
         const h = nodeSize(toolNode).h;
         positions.set(toolId, { x: toolX, y: curY });
         curY += h + TOOL_GAP_Y;
      }
   }

   // Any leftover nodes (notes inside component, failed placement)
   for (const n of componentNodes) {
      if (positions.has(n.id)) continue;
      positions.set(n.id, {
         x: (n.position?.x ?? 0),
         y: (n.position?.y ?? 0),
      });
   }

   return positions;
}

function normalizeComponentPositions(
   positions: Map<string, { x: number; y: number }>,
   componentNodes: Node[]
): { positions: Map<string, { x: number; y: number }>; width: number; height: number } {
   if (!componentNodes.length) {
      return { positions, width: 0, height: 0 };
   }

   let minX = Infinity;
   let minY = Infinity;
   let maxX = -Infinity;
   let maxY = -Infinity;

   for (const n of componentNodes) {
      const p = positions.get(n.id);
      if (!p) continue;
      const { w, h } = nodeSize(n);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + w);
      maxY = Math.max(maxY, p.y + h);
   }

   if (!Number.isFinite(minX)) {
      return { positions, width: 0, height: 0 };
   }

   const normalized = new Map<string, { x: number; y: number }>();
   for (const n of componentNodes) {
      const p = positions.get(n.id);
      if (!p) continue;
      normalized.set(n.id, { x: p.x - minX, y: p.y - minY });
   }

   return {
      positions: normalized,
      width: maxX - minX,
      height: maxY - minY,
   };
}

/**
 * Smart auto-arrange:
 * - Detects each independent flow (connected component)
 * - Lays out each flow left→right with clear layer spacing
 * - Stacks flows top→bottom (not side-by-side)
 * - Keeps AI Agent tools in a tidy column beside their agent
 * - Leaves generous gaps so edges stay readable
 */
export async function applyAutoLayout(
   nodes: Node[],
   edges: Edge[],
   direction: LayoutDirection = 'LR'
): Promise<Node[]> {
   if (!nodes.length) return nodes;

   const nodeById = new Map(nodes.map((n) => [n.id, n]));
   const components = findConnectedComponents(nodes, edges);
   const ordered = orderComponents(components, nodeById);

   const finalPos = new Map<string, { x: number; y: number }>();
   let cursorY = CANVAS_PAD;
   const originX = CANVAS_PAD;

   for (const comp of ordered) {
      const componentNodes = comp.nodeIds
         .map((id) => nodeById.get(id)!)
         .filter(Boolean);

      // Single note/orphan: park neatly without running full ELK
      if (componentNodes.length === 1 && isNoteNode(componentNodes[0])) {
         finalPos.set(componentNodes[0].id, { x: originX, y: cursorY });
         cursorY += nodeSize(componentNodes[0]).h + FLOW_GAP;
         continue;
      }

      const raw = await layoutComponent(componentNodes, edges, direction);
      const { positions, height } = normalizeComponentPositions(raw, componentNodes);

      for (const n of componentNodes) {
         const p = positions.get(n.id);
         if (!p) continue;
         finalPos.set(n.id, { x: originX + p.x, y: cursorY + p.y });
      }

      cursorY += Math.max(height, NODE_H) + FLOW_GAP;
   }

   const isHorizontal = direction === 'LR';

   return nodes.map((node) => {
      const pos = finalPos.get(node.id);
      if (!pos) return node;
      return {
         ...node,
         position: pos,
         positionAbsolute: pos,
         sourcePosition: (isHorizontal ? Position.Right : Position.Bottom) as Position,
         targetPosition: (isHorizontal ? Position.Left : Position.Top) as Position,
      };
   });
}
